import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import Vue from 'vue';

interface LoginCredentials {
  username: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  token?: string;
  user?: any;
  error?: string;
  message?: string;
}

interface TokenPayload {
  exp: number;
  iat: number;
  userId: string;
  username: string;
  role: string;
}

class AuthService {
  private api: AxiosInstance;
  private readonly tokenKey = 'fdpa_access_token';
  private readonly userKey = 'fdpa_user_data';
  private tokenCheckInterval: number | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      timeout: 10000,
    });

    console.log('🔧 AuthService initialized with API URL:', process.env.VUE_APP_API_URL);

    this.setupInterceptors();
    this.startTokenValidation();
  }

  /**
   * 🔧 Configurar interceptores de Axios
   */
  private setupInterceptors(): void {
    // Request interceptor - Agregar token a todas las requests
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token && this.isTokenValid(token)) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Manejar errores de autenticación
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const errorData = error.response.data;
          
          if (errorData.error === 'TOKEN_EXPIRED' || errorData.error === 'INVALID_TOKEN') {
            Vue.$toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
            this.logout();
          } else {
            Vue.$toast.error('No autorizado. Verifica tus credenciales.');
          }
        } else if (error.response?.status === 429) {
          Vue.$toast.warning('Demasiadas solicitudes. Espera un momento.');
        } else if (error.response?.status >= 500) {
          Vue.$toast.error('Error del servidor. Inténtalo más tarde.');
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * 🔑 Iniciar sesión
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      console.log('🔑 AuthService.login() called with:', { username: credentials.username });
      console.log('📡 Making request to:', `${this.api.defaults.baseURL}/users/login`);
      console.log('🌐 Base URL configured as:', this.api.defaults.baseURL);
      
      const response = await this.api.post('/users/login', credentials);
      console.log('📥 Login response:', response.status, response.data);
      
      const data = response.data;

      if (response.status === 200 && data.token) {
        console.log('✅ Login successful, storing token and user data');
        console.log('📋 User data received from backend:', JSON.stringify(data.user, null, 2));
        
        // Guardar token y datos de usuario
        this.setToken(data.token);
        this.setUser(data.user);
        
        // Construir nombre para mostrar
        const displayName = this.getUserDisplayName(data.user);
        console.log('👤 Display name will be:', displayName);
        Vue.$toast.success(`¡Bienvenido, ${displayName}!`);
        return { success: true, token: data.token, user: data.user };
      } else {
        console.warn('⚠️ Invalid response format:', data);
        return { success: false, error: 'INVALID_RESPONSE', message: 'Respuesta inválida del servidor' };
      }
    } catch (error: any) {
      console.error('❌ Login error:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        console.error('📡 Server error response:', errorData);
        return { 
          success: false, 
          error: errorData.error || 'LOGIN_ERROR', 
          message: errorData.message || 'Error al iniciar sesión' 
        };
      }
      
      console.error('🌐 Network or other error:', error.message);
      return { 
        success: false, 
        error: 'NETWORK_ERROR', 
        message: 'Error de conexión. Verifica tu conexión a internet.' 
      };
    }
  }

  /**
   * � Refrescar token JWT
   */
  async refreshToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;
    try {
      const response = await this.api.post('/users/refresh-token', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newToken = response.data?.token;
      if (newToken) {
        this.setToken(newToken);
        console.log('🔄 Token refrescado automáticamente');
        return true;
      }
      return false;
    } catch (error) {
      console.warn('⚠️ No se pudo refrescar el token:', error);
      return false;
    }
  }

  /**
   * �🚪 Cerrar sesión
   */
  logout(): void {
    this.clearToken();
    this.clearUser();
    this.stopTokenValidation();
    
    // Redireccionar al login
    window.location.href = '/';
  }

  /**
   * 🔍 Verificar si está autenticado
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null && this.isTokenValid(token);
  }

  /**
   * ⏰ Verificar si el token es válido (no expirado)
   */
  private isTokenValid(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      console.warn('Token inválido:', error);
      return false;
    }
  }

  /**
   * 🔓 Decodificar token JWT
   */
  private decodeToken(token: string): TokenPayload {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Token JWT inválido');
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  }

  /**
   * 📤 Obtener token
   */
  getToken(): string | null {
    return Cookies.get(this.tokenKey) || localStorage.getItem(this.tokenKey);
  }

  /**
   * 💾 Guardar token
   */
  private setToken(token: string): void {
    // Guardar en cookies (7 días) y localStorage como respaldo
    Cookies.set(this.tokenKey, token, { expires: 7, secure: false, sameSite: 'Lax' });
    localStorage.setItem(this.tokenKey, token);
  }

  /**
   * 🗑️ Eliminar token
   */
  private clearToken(): void {
    Cookies.remove(this.tokenKey);
    localStorage.removeItem(this.tokenKey);
  }

  /**
   * Obtener nombre para mostrar del usuario
   */
  getUserDisplayName(user: any): string {
    if (!user) return 'Usuario';
    
    console.log('🔍 Determining display name for user:', {
      fullName: user.fullName,
      firstName: user.firstName,
      lastName: user.lastName,
      name: user.name,
      username: user.username,
      allFields: Object.keys(user)
    });
    
    // Prioridad: fullName > firstName + lastName > name > username
    if (user.fullName) {
      console.log('✅ Using fullName:', user.fullName);
      return user.fullName;
    }
    
    if (user.firstName && user.lastName) {
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      console.log('✅ Using firstName + lastName:', fullName);
      return fullName;
    }
    
    if (user.firstName) {
      console.log('✅ Using firstName only:', user.firstName);
      return user.firstName;
    }
    
    if (user.name) {
      console.log('✅ Using name field:', user.name);
      return user.name;
    }
    
    console.log('⚠️ Falling back to username:', user.username);
    return user.username || 'Usuario';
  }

  /**
   * 👤 Obtener datos del usuario
   */
  getUser(): any {
    const userData = localStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * ♻️ Actualizar los datos almacenados del usuario
   */
  updateStoredUser(user: any): void {
    this.setUser(user);
  }

  /**
   * 💾 Guardar datos del usuario
   */
  private setUser(user: any): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  /**
   * 🗑️ Eliminar datos del usuario
   */
  private clearUser(): void {
    localStorage.removeItem(this.userKey);
  }

  /**
   * 🔄 Iniciar validación periódica del token
   */
  private startTokenValidation(): void {
    // Verificar token cada 5 minutos
    this.tokenCheckInterval = window.setInterval(() => {
      if (!this.isAuthenticated()) {
        console.warn('Token expirado detectado en validación periódica');
        this.logout();
      }
    }, 5 * 60 * 1000); // 5 minutos
  }

  /**
   * 🛑 Detener validación periódica del token
   */
  private stopTokenValidation(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  /**
   * 🔧 Obtener headers de autenticación
   */
  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  /**
   * 🌐 Instancia de API autenticada
   */
  getApiInstance(): AxiosInstance {
    return this.api;
  }

  /**
   * ⏱️ Tiempo restante del token en minutos
   */
  getTokenTimeRemaining(): number {
    const token = this.getToken();
    if (!token) return 0;

    try {
      const payload = this.decodeToken(token);
      const currentTime = Date.now() / 1000;
      const timeRemaining = payload.exp - currentTime;
      return Math.max(0, Math.floor(timeRemaining / 60)); // en minutos
    } catch (error) {
      return 0;
    }
  }

  /**
   * 🔄 Verificar si el token expira pronto (menos de 10 minutos)
   */
  isTokenExpiringSoon(): boolean {
    return this.getTokenTimeRemaining() < 10;
  }
}

// Instancia singleton
export const authService = new AuthService();
export default authService;
